import { Table, Spin, Row, Input, Tag } from 'antd';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';

import { transactionsService } from 'config/services';

import { HttpError } from 'helpers/http';

import { PaginationParams } from 'types/common';
import { TransactionOption } from 'types/services/articles';
import { PaginatedTransactions, Transaction } from 'types/services/transactions';
import { SessionState, StoreState } from 'types/store';

import { transformResource } from './utils';

import './index.scss';

const { Column } = Table;

export const Transactions = (props?: any) => {
  const { t } = useTranslation();
  const [pagination, setPagination] = useState<PaginationParams>(
    props.perPage ? { page: 1, perPage: props.perPage } : { page: 1, perPage: 10 },
  );

  const filterInputNode = useRef<typeof Input>(null);

  const { user } = useSelector<StoreState, SessionState>(({ session }) => session);

  const [filters, setFilters] = useState<TransactionOption>({});

  const {
    isLoading,
    isFetching,
    isError,
    data: response,
    error,
    refetch,
  } = useQuery<PaginatedTransactions, HttpError>(['listTransactions'], () =>
    transactionsService.list(pagination, filters),
  );
  //property perPage = 1000 due to retriving all users

  useEffect(() => {
    const timeOutId = setTimeout(() => {
      refetch();
    }, 1000);
    return () => clearTimeout(timeOutId);
  }, [pagination, filters, refetch]);

  function onPaginationChange(page: number) {
    setPagination((previousPagination) => ({ ...previousPagination, page }));
  }

  function onShowSizeChange(current: number, pageSize: number) {
    setPagination({ ...pagination, page: current, perPage: pageSize });
  }

  function transform(trans: Transaction[]) {
    return trans.map((transaction) => transformResource(transaction));
  }

  const translateStatus = (status: string) => {
    let color = 'green';
    if (status === 'rejected') {
      color = 'red';
    } else if (status === 'pending') {
      color = 'orange';
    }

    return <Tag color={color}>{t(`article:Status ${status}`)}</Tag>;
  };

  const handleTableChange = (
    pagination: any,
    filters: {
      status?: string[];
      category?: number[];
      author?: null | number[];
      textType?: string[];
      title?: string[];
    },
    sorter: any,
  ) => {
    let filterValues = omitBy(
      {
        status: filters.status,
        categoryIds: filters.category,
        authorId: filters.author,
        textType: filters.textType,
        title: filters.title,
      },
      isNil,
    );

    //setPagination((p) => ({ ...p, page: 1 }));
    if (Object.keys(filterValues).length === 0) {
      if (props.authorId !== -1 && props.authorId !== undefined) {
        filterValues.authorId = props.authorId;
        filterValues.textType = props.textType;
        filterValues.dateFrom = props.dateFrom;
        filterValues.dateTo = props.dateTo;
        filterValues.status = props.status;
        filterValues.title = props.title;
      }
    }

    if (props.dateFrom && props.dateTo) {
      filterValues.dateFrom = props.dateFrom;
      filterValues.dateTo = props.dateTo;
    }

    if (user?.role === 'EDITOR') filterValues.email = [user.email];
    setFilters(filterValues);
  };

  if (isError) {
    return (
      <div>
        <pre>{JSON.stringify(error, undefined, 2)}</pre>
      </div>
    );
  }

  if (!response) {
    return (
      <div style={{ padding: '10px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const emailFilterDropdown = () => (
    <div style={{ padding: 8 }}>
      <Input.Search
        value={filters.email}
        placeholder={t('common:Search term')}
        onChange={({ target }) => {
          setFilters((f) => ({ ...f, email: [target.value] }));
        }}
        onSearch={() => setPagination({ ...pagination, filters, page: 1 })}
        ref={(node) => {
          (filterInputNode as any).current = node;
        }}
      />
    </div>
  );

  const tranCodeFilterDropdown = () => (
    <div style={{ padding: 8 }}>
      <Input.Search
        value={filters.tranCode}
        placeholder={t('common:Search term')}
        onChange={({ target }) => {
          setFilters((f) => ({ ...f, tranCode: [target.value] }));
        }}
        onSearch={() => setPagination({ ...pagination, filters, page: 1 })}
        ref={(node) => {
          (filterInputNode as any).current = node;
        }}
      />
    </div>
  );

  const { data: transactions } = response;
  const { total } = response.pagination;

  return (
    <>
      <div style={{ padding: '10px', textAlign: 'center' }}>
        {
          <Row gutter={[8, 16]}>
            {/* <div>koje kolone ostaviti ? koje trebaju imati filter</div> */}
            <Table
              bordered
              sticky
              size="middle"
              loading={isLoading || isFetching}
              dataSource={transform(transactions)}
              pagination={{
                onChange: onPaginationChange,
                onShowSizeChange,
                size: 'default',
                position: ['bottomCenter'],
                showSizeChanger: true,
                showLessItems: true,
                current: pagination.page,
                pageSize: pagination.perPage,
                total,
              }}
              onChange={handleTableChange}
            >
              <Column key="id" dataIndex="id" title={'Broj transakcije'} width={100} />

              <Column key="obituaryId" dataIndex="obituaryId" title={'Broj smrtovnice'} width={100} />
              <Column key="phoneNumber" dataIndex="phoneNumber" title={'Broj telefona'} width={100} />
              <Column key="nameSurname" dataIndex="nameSurname" title={'Ime i prezime'} width={100} />

              <Column
                key="email"
                dataIndex="email"
                title={t('article:email')}
                filterDropdown={emailFilterDropdown}
                filteredValue={filters.email}
                width={100}
              />
              <Column
                key="totalAmount"
                dataIndex="totalAmount"
                title={'Ukupni iznos'}
                //#0000EE}
                width={95}
              />

              <Column
                key="tranCode"
                dataIndex="tranCode"
                title={t('article:tranCode')}
                filterDropdown={tranCodeFilterDropdown}
                //#0000EE}
                filteredValue={filters?.tranCode}
                width={80}
              />

              <Column key="recurrent" dataIndex="recurrent" title={t('article:recurrent')} width={100} />

              <Column key="orderId" dataIndex="orderId" title={t('article:orderId')} width={100} />
              <Column key="approvalCode" dataIndex="approvalCode" title={t('article:approvalCode')} width={100} />
              <Column key="proxyPan" dataIndex="proxyPan" title={t('article:proxyPan')} width={100} />
              <Column key="rrn" dataIndex="rrn" title={t('article:rrn')} width={100} />
              <Column key="purchaseTime" dataIndex="purchaseTime" title={t('article:purchaseTime')} width={100} />
              {/* <Column
                key="sd"
                dataIndex="sd"
                title={t('article:sd')}
                filters={textTypeFilterOptions}
                filteredValue={filters.textType}
                width={100}
              /> */}
              <Column key="xid" dataIndex="xid" title={t('article:xid')} width={100} />
              <Column key="currency" dataIndex="currency" title={t('article:currency')} width={100} />
              {/* <Column
                key="terminalId"
                dataIndex="terminalId"
                title={t('article:terminalId')}
                filters={textTypeFilterOptions}
                filteredValue={filters.textType}
                width={100}
              />
              <Column
                key="signature"
                dataIndex="signature"
                title={t('article:signature')}
                filters={textTypeFilterOptions}
                filteredValue={filters.textType}
                width={100}
              /> */}
            </Table>
          </Row>
        }
      </div>
    </>
  );
};
